import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Play, Save, Plus, Trash2, RefreshCw, X } from "lucide-react";
import {
  campaignsApi, scheduleConflict, Campaign, CampaignDetail, CampaignTier, CampaignCreate, CampaignUpdate,
  Forecast, SlotValue, Collision, ResolveResponse, LeadTimeShort, OverlapConflict, Audience, Visibility,
  PerUserReset, OnExhausted, CampaignKind, AudienceCount, Recurrence, CampaignTag, PreviewResponse, AlreadyStarted,
  CampaignBoost, BoostSurface, BOOST_SURFACES, BOOST_DEFAULTS, BOOST_BOUNDS,
} from "../../../api/maintenance/campaigns";
import CampaignPreview from "./CampaignPreview";
import CampaignArtwork from "./CampaignArtwork";
import CampaignTeasers from "./CampaignTeasers";
import { useNotice } from "../shared/useNotice";
import {
  money, num, ts, utcToIstInput, istInputToUtcIso, hoursUntil, readsAs, tierPays, tierBand, errMsg, statusPill,
  normalizeTag, normalizeTags, recurrenceLabel, MAX_TAGS, IMAGE_TYPES, MAX_IMAGE_KB, imageProblem,
} from "./campaignUtils";

interface Props {
  campaignId: number | null;          // null = new draft
  minLeadHours: number;
  canManage: boolean;
  canApprove: boolean;
  onChanged: () => void;              // list should refresh
  onClose: () => void;
}

type Form = {
  name: string; kind: CampaignKind; starts_at: string; ends_at: string;   // starts/ends as IST datetime-local strings
  audience: Audience; visibility: Visibility; org_ids: string;
  headline: string; subline: string; terms: string;
  budget_cap: string; max_awards: string; max_cashback_cap: string;
  per_user_award_limit: string; per_user_cap: string; per_user_reset: PerUserReset;
  on_budget_exhausted: OnExhausted; priority: string;
  announce_push: boolean; announce_inapp: boolean; announce_at: string; teaser_at: string; reminder_at: string;
  image_url: string;
  tags: string[];
  // recurrence, flattened for the form; assembled into the JSON on save
  rec_type: "" | "daily" | "weekly" | "monthly_first_n";
  rec_from: string; rec_to: string; rec_days: number[]; rec_n: string;
};

const blankForm = (): Form => {
  const start = new Date(Date.now() + 36 * 3600000); start.setMinutes(0, 0, 0);
  const end = new Date(start.getTime() + 7 * 86400000);
  return {
    name: "", kind: "offer", starts_at: utcToIstInput(start.toISOString()), ends_at: utcToIstInput(end.toISOString()),
    audience: "all", visibility: "public", org_ids: "", headline: "", subline: "", terms: "",
    budget_cap: "", max_awards: "", max_cashback_cap: "", per_user_award_limit: "", per_user_cap: "", per_user_reset: "campaign",
    on_budget_exhausted: "fall_back", priority: "", announce_push: true, announce_inapp: true, announce_at: "", teaser_at: "", reminder_at: "",
    image_url: "", tags: [],
    rec_type: "", rec_from: "20:00", rec_to: "23:59", rec_days: [6, 7], rec_n: "7",
  };
};

const fromCampaign = (c: Campaign): Form => ({
  tags: c.tags || [],
  rec_type: c.recurrence?.type ?? "",
  rec_from: c.recurrence && "from" in c.recurrence ? c.recurrence.from : "20:00",
  rec_to: c.recurrence && "to" in c.recurrence ? c.recurrence.to : "23:59",
  rec_days: c.recurrence && "days" in c.recurrence ? c.recurrence.days : [6, 7],
  rec_n: c.recurrence && "n" in c.recurrence ? String(c.recurrence.n) : "7",
  name: c.name, kind: c.kind, starts_at: utcToIstInput(c.starts_at), ends_at: utcToIstInput(c.ends_at),
  audience: c.audience, visibility: c.visibility, org_ids: (c.org_ids || []).join(","),
  headline: c.headline || "", subline: c.subline || "", terms: c.terms || "",
  budget_cap: c.budget_cap?.toString() || "", max_awards: c.max_awards?.toString() || "", max_cashback_cap: c.max_cashback_cap?.toString() || "",
  per_user_award_limit: c.per_user_award_limit?.toString() || "", per_user_cap: c.per_user_cap?.toString() || "",
  per_user_reset: c.per_user_reset, on_budget_exhausted: c.on_budget_exhausted, priority: c.priority?.toString() || "",
  announce_push: c.announce_push, announce_inapp: c.announce_inapp, announce_at: utcToIstInput(c.announce_at),
  teaser_at: utcToIstInput(c.teaser_at), reminder_at: utcToIstInput(c.reminder_at),
  image_url: c.image_url || "",
});

const numOrNull = (s: string): number | null => (s.trim() === "" ? null : Number(s));

/** The boost config while it is being edited. Numbers are held as strings the
 *  same way the main form holds them, so a half-typed box is a blank and not
 *  a NaN. Assembled back into the API shape on save. */
type BoostForm = {
  surfaces: BoostSurface[];
  per_7d: string; cooldown_hours: string; lifetime: string;
  social_proof: boolean; social_proof_min: string;
  priority: string; enabled: boolean;
  theme: "" | "diwali";   // "" is no theme, sent as null
};

const BOOST_NUMS = ["per_7d", "cooldown_hours", "lifetime", "social_proof_min", "priority"] as const;

/** A null boost column means the documented defaults, and a stored one is
 *  always whole because the server writes the entire validated object.
 *  Merged over the defaults anyway, so a key added later never lands as a
 *  blank box that would then be saved back as one. */
const boostForm = (b: CampaignBoost | null | undefined): BoostForm => {
  const v = { ...BOOST_DEFAULTS, ...(b || {}) };
  return {
    surfaces: v.surfaces || [],
    per_7d: String(v.per_7d), cooldown_hours: String(v.cooldown_hours), lifetime: String(v.lifetime),
    social_proof: v.social_proof, social_proof_min: String(v.social_proof_min),
    priority: String(v.priority), enabled: v.enabled,
    theme: v.theme ?? "",
  };
};

/** Surface order is not meaningful, so it must not make the form look dirty. */
const boostKey = (b: BoostForm) => JSON.stringify({ ...b, surfaces: [...b.surfaces].sort() });

const boundHint = (k: keyof typeof BOOST_BOUNDS) => {
  const [lo, hi] = BOOST_BOUNDS[k];
  return hi === null ? `${lo} or more` : `${lo}\u2013${hi}`;
};

const SURFACES: [BoostSurface, string, string][] = [
  ["badge", "Wallet tab dot", "a dot, no words"],
  ["home", "Card on the home screen", "the first screen everyone sees"],
  ["inline", "Card on the wallet screen", "where they weigh topping up"],
  ["topup", "Strip on the top-up screen", "the existing rate ticker"],
  ["post_session", "Sheet after a session", "the only interruptive one, not built yet"],
];

/** The flattened recurrence fields back into the JSON the server stores.
 *  "" means no recurrence at all: one window, the whole outer range, which is
 *  how every campaign behaved before run windows existed. */
const buildRecurrence = (f: Form): Recurrence | null => {
  if (f.rec_type === "daily") return { type: "daily", from: f.rec_from, to: f.rec_to };
  if (f.rec_type === "weekly") return { type: "weekly", days: [...f.rec_days].sort((a, b) => a - b), from: f.rec_from, to: f.rec_to };
  if (f.rec_type === "monthly_first_n") return { type: "monthly_first_n", n: Math.max(1, Number(f.rec_n) || 1) };
  return null;
};

const ISO_DAYS: [number, string][] = [[1, "Mon"], [2, "Tue"], [3, "Wed"], [4, "Thu"], [5, "Fri"], [6, "Sat"], [7, "Sun"]];

const emptyTier = (): CampaignTier => ({
  name: "", min_amount: 500, max_amount: null, is_random: false, min_percentage: null, max_percentage: null, fixed_percentage: 5, priority: 100, is_active: true,
});

// Presets: the shapes a campaign usually takes. Each sets the limit fields; everything stays editable.
const PRESETS: { key: string; name: string; sub: string; apply: (f: Form) => Form }[] = [
  { key: "flash", name: "Flash", sub: "first N recharges, short", apply: (f) => ({ ...f, max_awards: "100", budget_cap: "", per_user_award_limit: "1", per_user_reset: "campaign" }) },
  { key: "burst", name: "Budgeted burst", sub: "spend ₹X and stop", apply: (f) => ({ ...f, budget_cap: "25000", max_awards: "", per_user_award_limit: "", per_user_reset: "campaign" }) },
  { key: "habit", name: "Weekly habit", sub: "always on, N per week", apply: (f) => ({ ...f, budget_cap: "", max_awards: "", per_user_award_limit: "2", per_user_reset: "week" }) },
  { key: "targeted", name: "Targeted", sub: "a named list, no caps", apply: (f) => ({ ...f, budget_cap: "", max_awards: "", per_user_award_limit: "", audience: "list", visibility: "private" }) },
  { key: "none", name: "No limits", sub: "time only", apply: (f) => ({ ...f, budget_cap: "", max_awards: "", per_user_award_limit: "", per_user_cap: "", max_cashback_cap: "", per_user_reset: "campaign" }) },
];

const AUD: { key: Audience; name: string; sub: string }[] = [
  { key: "all", name: "Everyone", sub: "Public. Anyone who qualifies earns it." },
  { key: "list", name: "A list of people", sub: "Private. Only these customers see or earn it." },
  { key: "benefited", name: "Earned cashback before", sub: "Private segment, resolved when scheduled." },
  { key: "holding_promo", name: "Holding unspent cashback", sub: "Private segment, resolved when scheduled." },
  { key: "near_miss", name: "Near miss", sub: "Topped up just under the floor. Private segment." },
  { key: "dormant", name: "Dormant 60d+", sub: "Private segment, resolved when scheduled." },
];

const CampaignBuilder: React.FC<Props> = ({ campaignId, minLeadHours, canManage, canApprove, onChanged, onClose }) => {
  const [id, setId] = useState<number | null>(campaignId);
  const [detail, setDetail] = useState<CampaignDetail | null>(null);
  const [form, setForm] = useState<Form>(blankForm());
  const [tiers, setTiers] = useState<CampaignTier[]>([]);
  const [editTier, setEditTier] = useState<CampaignTier | null>(null);
  const [forecast, setForecast] = useState<Forecast | null>(null);
  const [slotValues, setSlotValues] = useState<SlotValue[]>([]);
  const [collisions, setCollisions] = useState<Collision[]>([]);
  const [audienceCounts, setAudienceCounts] = useState<Record<string, AudienceCount> | null>(null);
  const [conflict, setConflict] = useState<LeadTimeShort | OverlapConflict | AlreadyStarted | null>(null);
  const [waiverReason, setWaiverReason] = useState("");
  const [waiverErr, setWaiverErr] = useState("");
  const [phones, setPhones] = useState("");
  const [resolved, setResolved] = useState<ResolveResponse | null>(null);
  const [memberCount, setMemberCount] = useState(0);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useNotice("error", "");
  const [msg, setMsg] = useNotice("success", "");
  const [preset, setPreset] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [tagDraft, setTagDraft] = useState("");
  const [tagSuggestions, setTagSuggestions] = useState<CampaignTag[]>([]);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [boost, setBoost] = useState<BoostForm>(boostForm(null));
  const [otherImages, setOtherImages] = useState(0);

  const campaign = detail?.campaign || null;
  const editable = canManage && (!campaign || campaign.status === "draft");
  // Artwork outlives the draft. Rates, dates and limits freeze the moment a
  // campaign is committed, but a picture is not a money row, and a campaign
  // that went live without one used to be stuck without one for its whole run.
  // Ended and cancelled stay fixed: no audience left to show it to.
  const artworkEditable = canManage && (!campaign || ["draft", "scheduled", "live"].includes(campaign.status));
  const artworkLateEdit = artworkEditable && !editable;
  // Same gate, same reason. A campaign showing itself too often is a live
  // problem, and "end it and schedule another" is not an answer to one -- it
  // would also lose the offer customers are part-way through. So PUT
  // /campaigns/{id}/boost takes draft, scheduled and live, where the
  // draft-only `editable` does not.
  const boostEditable = canManage && (!campaign || ["draft", "scheduled", "live"].includes(campaign.status));
  const boostLateEdit = boostEditable && !editable;
  const boostDirty = boostKey(boost) !== boostKey(boostForm(campaign?.boost));
  // A teaser is a push before the start, so a scheduled campaign can still
  // gain or lose one. Once live there is nothing left to tease.
  const teasersEditable = canManage && (!campaign || ["draft", "scheduled"].includes(campaign.status));

  const load = useCallback(async (cid: number) => {
    try {
      const r = await campaignsApi.get(cid);
      setDetail(r.data);
      setForm(fromCampaign(r.data.campaign));
      setTiers(r.data.tiers);
      setSlotValues(r.data.slot_values);
      setMemberCount(r.data.member_count);
      setBoost(boostForm(r.data.campaign.boost));
      if (r.data.campaign.forecast) setForecast(r.data.campaign.forecast);
    } catch (e: any) { setErr(errMsg(e, "Failed to load campaign")); }
  }, []);

  useEffect(() => { if (campaignId) load(campaignId); }, [campaignId, load]);

  // Suggest from the tags already in use, so the vocabulary converges on
  // "diwali-2026" instead of sprouting a variant per campaign. Reads the tag
  // table directly rather than pulling a campaign list purely for its tags.
  useEffect(() => {
    campaignsApi.tags()
      .then((r) => setTagSuggestions(r.data))
      .catch(() => { /* suggestions are a convenience; a failure must not block the form */ });
  }, []);

  // The preview is rendered from the SERVER's copy, not rebuilt here: the same
  // _copy() the dispatcher sends with. Debounced because it follows typing, and
  // failures are swallowed -- a preview is a convenience and must never be the
  // reason a campaign cannot be saved.
  useEffect(() => {
    if (!form.name.trim()) { setPreview(null); return; }
    let cancelled = false;
    setPreviewBusy(true);
    const timer = setTimeout(() => {
      campaignsApi.preview({
        name: form.name,
        kind: form.kind,
        headline: form.headline.trim() || null,
        subline: form.subline.trim() || null,
        starts_at: form.starts_at ? istInputToUtcIso(form.starts_at) : null,
        ends_at: form.ends_at ? istInputToUtcIso(form.ends_at) : null,
        image_url: form.image_url.trim() || null,
        announce_push: form.announce_push,
        announce_inapp: form.announce_inapp,
      })
        .then((r) => { if (!cancelled) setPreview(r.data); })
        .catch(() => { /* convenience only */ })
        .finally(() => { if (!cancelled) setPreviewBusy(false); });
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [form.name, form.kind, form.headline, form.subline, form.starts_at, form.ends_at,
      form.image_url, form.announce_push, form.announce_inapp]);

  const patch = (p: Partial<Form>) => setForm((f) => ({ ...f, ...p }));

  const isAnnouncement = form.kind === "announcement";

  const payload = (): CampaignCreate => ({
    name: form.name, kind: form.kind,
    starts_at: istInputToUtcIso(form.starts_at), ends_at: istInputToUtcIso(form.ends_at),
    audience: form.audience, visibility: form.visibility,
    org_ids: form.org_ids.trim() ? form.org_ids.split(",").map((s) => Number(s.trim())).filter((n) => !isNaN(n)) : null,
    headline: form.headline || null, subline: form.subline || null, terms: form.terms || null,
    budget_cap: numOrNull(form.budget_cap), max_awards: numOrNull(form.max_awards), max_cashback_cap: numOrNull(form.max_cashback_cap),
    per_user_award_limit: numOrNull(form.per_user_award_limit), per_user_cap: numOrNull(form.per_user_cap), per_user_reset: form.per_user_reset,
    on_budget_exhausted: form.on_budget_exhausted, priority: numOrNull(form.priority),
    announce_push: form.announce_push, announce_inapp: form.announce_inapp,
    announce_at: form.announce_at ? istInputToUtcIso(form.announce_at) : null,
    // An announcement IS the message: no opening to trail, no closing to warn
    // about. The server clears these for that kind anyway and the table has a
    // CHECK behind it; sending null keeps the three in agreement rather than
    // letting the console quietly carry a value nothing will ever use.
    teaser_at: isAnnouncement || !form.teaser_at ? null : istInputToUtcIso(form.teaser_at),
    reminder_at: isAnnouncement || !form.reminder_at ? null : istInputToUtcIso(form.reminder_at),
    image_url: form.image_url.trim() || null,
    tags: normalizeTags(form.tags),
    recurrence: buildRecurrence(form),
  });

  const saveDraft = async (): Promise<number | null> => {
    setErr(""); setMsg(""); setBusy("save");
    try {
      if (!form.name.trim()) throw new Error("Give the campaign a name");
      if (id == null) {
        const r = await campaignsApi.create({ ...payload(), tiers });
        setId(r.data.id);
        await load(r.data.id);
        setMsg("Draft created.");
        onChanged();
        return r.data.id;
      }
      const p = payload();
      const clear = (Object.keys(p) as (keyof CampaignCreate)[]).filter((k) => p[k] === null && k !== "tiers").map(String);
      const upd: CampaignUpdate = { ...p, clear };
      await campaignsApi.update(id, upd);
      await load(id);
      setMsg("Draft saved.");
      onChanged();
      return id;
    } catch (e: any) { setErr(e?.message && !e?.response ? e.message : errMsg(e, "Save failed")); return null; }
    finally { setBusy(""); }
  };

  const saveTier = async () => {
    if (!editTier) return;
    setErr("");
    try {
      const cid = id ?? (await saveDraft());
      if (cid == null) return;
      await campaignsApi.upsertTier(cid, editTier);
      setEditTier(null);
      await load(cid);
      setForecast(null);
    } catch (e: any) { setErr(errMsg(e, "Slot save failed")); }
  };

  const removeTier = async (tid?: number) => {
    if (!tid || !id || !window.confirm("Remove this slot?")) return;
    try { await campaignsApi.deleteTier(id, tid); await load(id); setForecast(null); }
    catch (e: any) { setErr(errMsg(e, "Remove failed")); }
  };

  const runForecast = async () => {
    setErr(""); setBusy("forecast");
    try {
      const cid = id ?? (await saveDraft());
      if (cid == null) return;
      if (id != null) await campaignsApi.update(cid, { ...payload(), clear: [] });
      const [f, c] = await Promise.all([campaignsApi.forecast(cid), campaignsApi.collisions(cid)]);
      setForecast(f.data.forecast); setSlotValues(f.data.slot_values); setCollisions(c.data.collisions);
      if (form.audience !== "all") {
        const a = await campaignsApi.audience(cid); setAudienceCounts(a.data.counts);
      }
    } catch (e: any) { setErr(errMsg(e, "Forecast failed")); }
    finally { setBusy(""); }
  };

  const resolvePhones = async () => {
    if (!id) { setErr("Save the draft first"); return; }
    const list = phones.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    if (!list.length) return;
    setBusy("resolve");
    try { const r = await campaignsApi.resolveMembers(id, list); setResolved(r.data); }
    catch (e: any) { setErr(errMsg(e, "Could not resolve numbers")); }
    finally { setBusy(""); }
  };

  const addMembers = async () => {
    if (!id || !resolved) return;
    setBusy("members");
    try {
      const r = await campaignsApi.addMembers(id, resolved.rows.filter((x) => x.matched).map((x) => x.phone));
      setMsg(`${r.data.added} added · ${r.data.count} on the list.`);
      setResolved(null); setPhones("");
      await load(id);
    } catch (e: any) { setErr(errMsg(e, "Add failed")); }
    finally { setBusy(""); }
  };

  const schedule = async (acceptOverlap = false) => {
    setErr(""); setMsg(""); setConflict(null); setBusy("schedule");
    try {
      const cid = id ?? (await saveDraft());
      if (cid == null) return;
      if (id != null) await campaignsApi.update(cid, { ...payload(), clear: [] });
      if (!window.confirm(acceptOverlap
        ? "Schedule with the overlap as shown? After this the campaign cannot be edited."
        : "Schedule this campaign? After this it cannot be edited — only ended.")) return;
      const r = await campaignsApi.schedule(cid, acceptOverlap);
      setMsg(`Scheduled. Goes live ${ts(r.data.starts_at)}.`);
      await load(cid); onChanged();
    } catch (e: any) {
      const c = scheduleConflict(e);
      // already_started was typed but never rendered, so once a start time slid
      // into the past the card -- and the override button inside it -- silently
      // vanished mid-interaction. With a short lead that happens in minutes.
      if (c && (c.detail === "lead_time_short" || c.detail === "overlap" || c.detail === "already_started")) {
        setConflict(c as LeadTimeShort | OverlapConflict | AlreadyStarted);
      }
      else setErr(errMsg(e, "Schedule failed"));
    } finally { setBusy(""); }
  };

  const raiseWaiver = async () => {
    // Shown inside the conflict card as well. The shared error banner sits at
    // the very top of the drawer and this button is near the bottom, so the
    // only feedback a short reason produced was off-screen -- the button read
    // as dead.
    setWaiverErr("");
    if (!id) return;
    if (waiverReason.trim().length < 10) {
      setWaiverErr(`Give a reason of at least 10 characters (you have ${waiverReason.trim().length}).`);
      return;
    }
    setBusy("waiver");
    try {
      await campaignsApi.raiseLaunchRequest(id, waiverReason.trim());
      setMsg("Override requested. A super-admin has to approve it before this can be scheduled.");
      setConflict(null); setWaiverReason(""); await load(id); onChanged();
    } catch (e: any) { const m = errMsg(e, "Request failed"); setWaiverErr(m); setErr(m); }
    finally { setBusy(""); }
  };

  const cancel = async () => {
    if (!id || !window.confirm("Cancel this campaign?")) return;
    try { await campaignsApi.cancel(id); await load(id); onChanged(); } catch (e: any) { setErr(errMsg(e, "Cancel failed")); }
  };
  const end = async () => {
    if (!id || !window.confirm("End this LIVE campaign now? Rewards already given stand; nothing more is paid at these rates.")) return;
    try { await campaignsApi.end(id); await load(id); onChanged(); } catch (e: any) { setErr(errMsg(e, "End failed")); }
  };

  // ---- derived
  const lead = useMemo(() => {
    const iso = istInputToUtcIso(form.starts_at);
    if (!iso) return null;
    const h = hoursUntil(iso);
    return { hours: h, ok: h >= minLeadHours, started: h <= 0, short: Math.max(0, minLeadHours - h) };
  }, [form.starts_at, minLeadHours]);

  const binds = forecast?.window.binds_first || null;
  const windowDays = forecast?.window_days || 0;
  const verdict = !forecast ? null : binds
    ? { main: <>The <b>{binds.limit === "max_awards" ? "reward count" : "budget"}</b>, on about <b>day {binds.on_day}</b> of {Math.round(windowDays)}</>,
        sub: `${binds.limit === "max_awards" ? `${num(forecast.window.max_awards)} rewards handed out` : `${money(forecast.window.budget_cap)} spent`} · roughly ${money(binds.spent_when_bound)} spent when it binds · ${num(forecast.window.topups)} top-ups would have qualified` }
    : { main: <>The <b>end date</b> — no limit binds first</>,
        sub: `${num(forecast.window.topups)} top-ups · ${money(forecast.window.payout)} forecast${forecast.window.budget_cap ? ` · ${forecast.window.budget_share_used}% of budget` : ""}` };

  const uploadImage = async (file: File) => {
    setErr(""); setMsg("");
    const bad = imageProblem(file);
    if (bad) { setErr(bad); return; }
    setBusy("image");
    try {
      // Artwork is stored under the campaign's own id, so a brand-new draft has
      // to exist before it can carry one. Same order the slot editor uses.
      const cid = id ?? (await saveDraft());
      if (cid == null) return;
      const r = await campaignsApi.uploadImage(cid, file);
      // Patch rather than reload: the response already carries the stored URL,
      // and a full load() would throw away whatever else is unsaved on the form.
      patch({ image_url: r.data.image_url || "" });
      setMsg("Artwork uploaded.");
      onChanged();
    } catch (e: any) { setErr(errMsg(e, "Upload failed")); }
    finally { setBusy(""); }
  };

  const removeImage = async () => {
    setErr(""); setMsg("");
    // Nothing is stored yet for an unsaved draft, so clearing the field is all
    // there is to do.
    if (id == null) { patch({ image_url: "" }); return; }
    setBusy("image");
    try {
      await campaignsApi.deleteImage(id);
      patch({ image_url: "" });
      setMsg("Artwork removed. The push will go out as plain text.");
      onChanged();
    } catch (e: any) { setErr(errMsg(e, "Could not remove the artwork")); }
    finally { setBusy(""); }
  };

  const toggleSurface = (k: BoostSurface) =>
    setBoost((b) => ({
      ...b,
      // rebuilt in the canonical order rather than appended, so the stored
      // list does not depend on which box was ticked first
      surfaces: BOOST_SURFACES.filter((x) => (x === k ? !b.surfaces.includes(k) : b.surfaces.includes(x))),
    }));

  /** A REPLACE, not a patch: the whole config goes every time, because a key
   *  left out of the body takes the documented default server-side rather
   *  than keeping whatever was stored. */
  const saveBoost = async () => {
    setErr(""); setMsg("");
    if (BOOST_NUMS.some((k) => boost[k].trim() === "")) {
      setErr("Every box needs a number \u2014 saving sends the whole config, not just what changed.");
      return;
    }
    const body: CampaignBoost = {
      surfaces: boost.surfaces,
      per_7d: Number(boost.per_7d), cooldown_hours: Number(boost.cooldown_hours), lifetime: Number(boost.lifetime),
      social_proof: boost.social_proof, social_proof_min: Number(boost.social_proof_min),
      priority: Number(boost.priority), enabled: boost.enabled,
      // Left out when there is none: the PUT replaces the whole config, so a
      // missing key is the default (no theme), and an admin-api that predates
      // themes still accepts every save that does not pick one.
      ...(boost.theme ? { theme: boost.theme } : {}),
    };
    // Boost is stored on the campaign row, so a brand-new draft has to exist
    // first. Same order the slot editor and the artwork upload use.
    const cid = id ?? (await saveDraft());
    if (cid == null) return;
    setBusy("boost");
    try {
      const r = await campaignsApi.setBoost(cid, body);
      // Patch rather than reload, as the artwork upload does: the response is
      // the stored campaign, and a load() would throw away anything else
      // unsaved on the form.
      setDetail((d) => (d ? { ...d, campaign: r.data } : d));
      setBoost(boostForm(r.data.boost));
      setMsg("Promotion settings saved.");
      onChanged();
    } catch (e: any) {
      // The server refuses an out-of-range cap instead of quietly reducing it,
      // and the 400 says which number and what the range is. Show that
      // sentence -- a generic "save failed" loses the only useful part.
      setErr(errMsg(e, "Could not save the promotion settings"));
    } finally { setBusy(""); }
  };

  const field = (label: string, node: React.ReactNode, hint?: string) => (
    <label className="ma-select">{label}{node}{hint && <span className="ma-hint">{hint}</span>}</label>
  );
  const inp = (k: keyof Form, type = "text", placeholder = "") => (
    <input type={type} value={form[k] as string} placeholder={placeholder} disabled={!editable}
      onChange={(e) => patch({ [k]: e.target.value } as any)} style={{ border: "1px solid #e6e8ee", borderRadius: 7, padding: "6px 8px", fontSize: 13 }} />
  );
  const sel = (k: keyof Form, opts: [string, string][]) => (
    <select value={form[k] as string} disabled={!editable} onChange={(e) => patch({ [k]: e.target.value } as any)}>
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );

  // Bounded on the input as well, but the server is the authority: it refuses
  // an out-of-range value rather than clamping it, and says so in the 400.
  const bnum = (k: (typeof BOOST_NUMS)[number]) => {
    const [lo, hi] = BOOST_BOUNDS[k];
    return (
      <input type="number" value={boost[k]} min={lo} max={hi ?? undefined} disabled={!boostEditable}
        onChange={(e) => setBoost((b) => ({ ...b, [k]: e.target.value } as BoostForm))}
        style={{ border: "1px solid #e6e8ee", borderRadius: 7, padding: "6px 8px", fontSize: 13 }} />
    );
  };

  const addTag = (raw: string) => {
    const t = normalizeTag(raw);
    if (!t) { setTagDraft(""); return; }
    if (form.tags.length >= MAX_TAGS && !form.tags.includes(t)) {
      setErr(`A campaign can carry ${MAX_TAGS} tags.`);
      return;
    }
    patch({ tags: normalizeTags([...form.tags, t]) });
    setTagDraft("");
  };
  const removeTag = (t: string) => patch({ tags: form.tags.filter((x) => x !== t) });

  // What the operator typed, as it will actually be stored — shown live so a
  // stray capital or space is visibly folded away rather than silently.
  const tagPreview = normalizeTag(tagDraft);

  // Which utm_campaign a link in this campaign's messages will carry. Nobody
  // types it here: it comes from the tag, which is the point of tagging an
  // offer and the announcement promoting it the same thing — both attribute to
  // one name. Falls back to the campaign's own slug. Shown so the answer is
  // not invisible until the day a link actually goes out.
  const attribution = (() => {
    for (const slug of form.tags) {
      const t = tagSuggestions.find((x) => x.slug === slug);
      if (t?.utm_campaign) return { name: t.utm_campaign, via: `from the ${t.label || t.slug} tag` };
    }
    return campaign?.slug ? { name: campaign.slug, via: "this campaign's own slug, as no tag names one" } : null;
  })();
  // Most-used first, so the vocabulary that already exists is the easiest
  // thing to reach for and stays the one that gets reused.
  const unusedSuggestions = tagSuggestions
    .filter((t) => !form.tags.includes(t.slug))
    .sort((a, b) => b.usage_count - a.usage_count)
    .slice(0, 8);

  const tagsCard = (
    <label className="ma-select" style={{ gridColumn: "1 / -1" }}>Tags
      <div style={{ display: "flex", flexWrap: "wrap", gap: 5, alignItems: "center", marginTop: 3 }}>
        {form.tags.map((t) => (
          <span key={t} className="ma-pill info" style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            {t}
            {editable && (
              <button onClick={() => removeTag(t)} title={`Remove ${t}`}
                style={{ border: "none", background: "none", cursor: "pointer", padding: 0, lineHeight: 1, color: "inherit" }}>
                <X size={11} />
              </button>
            )}
          </span>
        ))}
        {editable && (
          <input value={tagDraft} placeholder={form.tags.length ? "add another…" : "diwali-2026"}
            onChange={(e) => setTagDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(tagDraft); }
              else if (e.key === "Backspace" && !tagDraft && form.tags.length) removeTag(form.tags[form.tags.length - 1]);
            }}
            onBlur={() => addTag(tagDraft)}
            style={{ border: "1px solid #e6e8ee", borderRadius: 7, padding: "5px 8px", fontSize: 13, minWidth: 130 }} />
        )}
      </div>
      {editable && tagPreview && tagPreview !== tagDraft.trim() && (
        <span className="ma-hint">will be stored as <b>{tagPreview}</b></span>
      )}
      {editable && unusedSuggestions.length > 0 && (
        <span style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 5, alignItems: "center" }}>
          <span className="ma-hint">in use:</span>
          {unusedSuggestions.map((t) => (
            <button key={t.slug} onClick={() => addTag(t.slug)} className="ma-pill muted"
              title={t.description || t.slug}
              style={{ border: "none", cursor: "pointer", font: "inherit", fontSize: 10, fontWeight: 600 }}>
              + {t.label || t.slug}
              <span style={{ opacity: 0.6, marginLeft: 4 }}>{t.usage_count}</span>
            </button>
          ))}
        </span>
      )}
      <span className="ma-hint">
        Groups a campaign with the ones it belongs to — an offer and the announcement promoting it under one tag.
        Also becomes <code>utm_campaign</code> when a message carries a link.
      </span>
      {attribution && (
        <span className="ma-hint">
          Links will attribute to <b>{attribution.name}</b> — {attribution.via}.
        </span>
      )}
    </label>
  );

  const windows = campaign?.windows || [];
  const recurrenceCard = (
    <div style={{ gridColumn: "1 / -1", borderTop: "1px solid #e6e8ee", paddingTop: 12, marginTop: 4 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 12 }}>
        {field("When it actually runs", sel("rec_type", [
          ["", "Continuously, between the dates"],
          ["daily", "Daily, certain hours"],
          ["weekly", "Certain days of the week"],
          ["monthly_first_n", "First N days of each month"],
        ]), "the dates above stay the outer limits; this is when it pays inside them")}

        {(form.rec_type === "daily" || form.rec_type === "weekly") && (<>
          {field("From (IST)", inp("rec_from", "time"))}
          {field("To (IST)", inp("rec_to", "time"))}
        </>)}

        {form.rec_type === "monthly_first_n" &&
          field("How many days", inp("rec_n", "number", "7"), "counted from the 1st, in the customer's month")}
      </div>

      {form.rec_type === "weekly" && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
          {ISO_DAYS.map(([d, lbl]) => (
            <button key={d} disabled={!editable}
              className={`ma-preset ${form.rec_days.includes(d) ? "on" : ""}`}
              style={{ border: "1px solid #e6e8ee", borderRadius: 7 }}
              onClick={() => patch({ rec_days: form.rec_days.includes(d) ? form.rec_days.filter((x) => x !== d) : [...form.rec_days, d] })}>
              {lbl}
            </button>
          ))}
        </div>
      )}

      {form.rec_type !== "" && (
        <div className="ma-note" style={{ marginTop: 10, marginBottom: 0 }}>
          <b>{recurrenceLabel(buildRecurrence(form))}.</b> The exact windows are worked out when you schedule and
          frozen, so what it will do is on the record rather than recomputed later. Between windows the campaign is
          still live — it simply is not paying, and the app shows the next window instead of an offer it will not honour.
        </div>
      )}

      {windows.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div className="ma-hint" style={{ marginBottom: 4 }}>{num(windows.length)} run window{windows.length === 1 ? "" : "s"}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5, maxHeight: 132, overflowY: "auto" }}>
            {windows.map((w, i) => (
              <span key={i} className="ma-pill muted" style={{ fontSize: 10 }}>{ts(w.starts_at)} → {ts(w.ends_at)}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // The campaign's own image, and the default whenever no extra image applies.
  // Upload, remove and the pasted link behave as they always have; the card
  // around it is CampaignArtwork.
  const mainImage = (
    <div className="ma-select">
      <span style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <input type="file" accept={IMAGE_TYPES.join(",")} disabled={!artworkEditable || busy === "image"}
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadImage(f); }}
          style={{ fontSize: 13 }} />
        {busy === "image" && <span className="ma-hint">uploading…</span>}
        {artworkEditable && busy !== "image" && form.image_url.trim() !== "" && (
          <button className="ma-btn" type="button" onClick={removeImage}><Trash2 size={13} /> Remove</button>
        )}
      </span>
      <span className="ma-hint">PNG, JPEG or WebP, under {MAX_IMAGE_KB}KB. Shown whenever no other image applies.</span>
      {artworkLateEdit ? (
        <span className="ma-hint">Upload only — a link cannot be changed once scheduled. A push already sent keeps its old picture.</span>
      ) : (
        field("… or paste a link to an image already hosted", inp("image_url", "text", "https://…"), "A public https:// link.")
      )}
      {/* keyed on the URL so a corrected link re-mounts and gets a fresh chance to load */}
      {form.image_url.trim() && (
        <img key={form.image_url} src={form.image_url.trim()} alt=""
          onError={(e) => { e.currentTarget.style.display = "none"; }}
          style={{ marginTop: 6, maxHeight: 96, maxWidth: "100%", alignSelf: "flex-start", borderRadius: 6, border: "1px solid #e6e8ee" }} />
      )}
    </div>
  );

  return (
    <div className="ma">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <button className="ma-btn" onClick={onClose}><X size={14} /> Back</button>
        <h3 style={{ margin: 0, fontSize: 15 }}>{campaign ? campaign.name : "New campaign"}</h3>
        {campaign && <span className={`ma-pill ${statusPill(campaign.status)}`}>{campaign.status}</span>}
        {campaign?.lead_waiver_id && <span className="ma-pill info">lead-time waiver #{campaign.lead_waiver_id}</span>}
        <span className="ma-spacer" />
        {editable && <button className="ma-btn" disabled={!!busy} onClick={saveDraft}><Save size={14} /> Save draft</button>}
        {campaign && (campaign.status === "draft" || campaign.status === "scheduled") && canManage && <button className="ma-btn" onClick={cancel}>Cancel campaign</button>}
        {campaign?.status === "live" && canApprove && <button className="ma-btn" style={{ color: "#c62828" }} onClick={end}>End now</button>}
      </div>

      {err && <div className="ma-error">{err}</div>}
      {msg && <div className="ma-note">{msg}</div>}
      {detail?.pending_launch_request && (
        <div className="ma-note">Override request pending since {ts(detail.pending_launch_request.requested_at)} — {detail.pending_launch_request.lead_hours_actual}h notice against {detail.pending_launch_request.lead_hours_required}h. Waiting on a super-admin.</div>
      )}

      {/* ---- 1 name & window */}
      <div className="ma-card" style={{ marginBottom: 14 }}>
        <div className="ma-card-h"><h3>1 · Name and window</h3><span className="ma-hint">{editable ? "editable until you schedule" : "frozen"}</span></div>
        <div className="ma-card-b" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 12 }}>
          {field("Campaign name", inp("name", "text", "Diwali Double"))}
          {field("Type", sel("kind", [["offer", "Offer — pays cashback"], ["announcement", "Announcement — message only"]]))}
          {field("Starts (IST)", inp("starts_at", "datetime-local"))}
          {field("Ends (IST)", inp("ends_at", "datetime-local"))}
          {field("Headline (what customers read)", inp("headline", "text", "Up to 14% back on every top-up"))}
          {field("Subline", inp("subline", "text", "Friday 8 PM to Sunday midnight"))}
          {tagsCard}
          {recurrenceCard}
        </div>
        {lead && (
          <div className="ma-card-b" style={{ paddingTop: 0 }}>
            <div style={{ border: `1px solid ${lead.ok ? "#2e7d32" : lead.started ? "#c62828" : "#e65100"}`, background: lead.ok ? "#e6f6ea" : lead.started ? "#fdeaea" : "#fff3e0", borderRadius: 9, padding: "11px 14px" }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>
                {lead.started ? "Start time has already passed" : lead.ok ? `✓ Starting in ${lead.hours.toFixed(1)} hours` : `⚠ ${lead.short.toFixed(1)} hours short`}
              </div>
              <div style={{ height: 7, borderRadius: 4, background: "rgba(0,0,0,.08)", margin: "8px 0 6px", overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${Math.max(0, Math.min(100, (lead.hours / minLeadHours) * 100))}%`, background: lead.ok ? "#2e7d32" : "#e65100" }} />
              </div>
              <div className="ma-hint">
                {lead.ok ? `Past the ${minLeadHours}-hour minimum. The app can show "starts ${ts(istInputToUtcIso(form.starts_at))}" the moment you schedule.`
                  : lead.started ? "Move the start forward." : `A campaign must start at least ${minLeadHours} hours from the moment you press Schedule. You can still do it with a super-admin override, reason on the record.`}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ---- 2 rate card */}
      {form.kind === "offer" && (
        <div className="ma-card" style={{ marginBottom: 14 }}>
          <div className="ma-card-h"><h3>2 · Rate card <span className="ma-hint">· first matching slot wins, by priority</span></h3>
            {editable && <button className="ma-btn" onClick={() => setEditTier(emptyTier())}><Plus size={14} /> Add slot</button>}</div>
          {editTier && (
            <div className="ma-card-b" style={{ background: "#f7f8ff", borderBottom: "1px solid #e6e8ee", display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(130px,1fr))", gap: 10, alignItems: "end" }}>
              <label className="ma-select">Name<input value={editTier.name} onChange={(e) => setEditTier({ ...editTier, name: e.target.value })} /></label>
              <label className="ma-select">Min ₹<input type="number" value={editTier.min_amount} onChange={(e) => setEditTier({ ...editTier, min_amount: Number(e.target.value) })} /></label>
              <label className="ma-select">Max ₹ (blank = no ceiling)<input type="number" value={editTier.max_amount ?? ""} onChange={(e) => setEditTier({ ...editTier, max_amount: e.target.value === "" ? null : Number(e.target.value) })} /></label>
              <label className="ma-select">Pays<select value={editTier.is_random ? "1" : "0"} onChange={(e) => setEditTier({ ...editTier, is_random: e.target.value === "1" })}><option value="0">Fixed</option><option value="1">Random range</option></select></label>
              {editTier.is_random ? (<>
                <label className="ma-select">Min %<input type="number" value={editTier.min_percentage ?? ""} onChange={(e) => setEditTier({ ...editTier, min_percentage: e.target.value === "" ? null : Number(e.target.value) })} /></label>
                <label className="ma-select">Max %<input type="number" value={editTier.max_percentage ?? ""} onChange={(e) => setEditTier({ ...editTier, max_percentage: e.target.value === "" ? null : Number(e.target.value) })} /></label>
              </>) : (
                <label className="ma-select">Fixed %<input type="number" value={editTier.fixed_percentage ?? ""} onChange={(e) => setEditTier({ ...editTier, fixed_percentage: e.target.value === "" ? null : Number(e.target.value) })} /></label>
              )}
              <label className="ma-select">Priority<input type="number" value={editTier.priority} onChange={(e) => setEditTier({ ...editTier, priority: Number(e.target.value) })} /></label>
              <div style={{ display: "flex", gap: 6 }}><button className="ma-btn primary" onClick={saveTier}>Save</button><button className="ma-btn" onClick={() => setEditTier(null)}>Cancel</button></div>
            </div>
          )}
          <div className="ma-tablewrap"><table className="ma-table">
            <thead><tr><th>Slot</th><th>Top-up band</th><th>Pays</th><th className="num">₹100 becomes</th><th className="num">Charging discount</th><th className="num">≈ free kWh at ₹1,000</th><th className="num">≈ sessions</th>{editable && <th />}</tr></thead>
            <tbody>
              {tiers.map((t) => {
                const sv = slotValues.find((s) => s.tier_id === t.id);
                const rng = (a: number, b: number, f: (x: number) => string) => (a === b ? f(a) : `${f(a)} – ${f(b)}`);
                return (
                  <tr key={t.id} style={{ opacity: t.is_active ? 1 : 0.5 }}>
                    <td><b>{t.name}</b></td><td>{tierBand(t)}</td><td>{tierPays(t)}</td>
                    <td className="num">{sv ? rng(sv.lo.per_100_becomes, sv.hi.per_100_becomes, (x) => `₹${x.toFixed(2)}`) : "—"}</td>
                    <td className="num">{sv ? rng(sv.lo.effective_discount_pct, sv.hi.effective_discount_pct, (x) => `${x.toFixed(1)}%`) : "—"}</td>
                    <td className="num">{sv ? rng(sv.lo.free_kwh_on_basis, sv.hi.free_kwh_on_basis, (x) => x.toFixed(1)) : "—"}</td>
                    <td className="num">{sv ? rng(sv.lo.free_sessions_on_basis, sv.hi.free_sessions_on_basis, (x) => x.toFixed(1)) : "—"}</td>
                    {editable && <td><div style={{ display: "flex", gap: 6 }}><button className="ma-btn" onClick={() => setEditTier(t)}>Edit</button><button className="ma-btn" onClick={() => removeTier(t.id)}><Trash2 size={13} /></button></div></td>}
                  </tr>
                );
              })}
              {!tiers.length && <tr><td colSpan={8} className="ma-empty">No slots yet{id == null ? " — add one (saves the draft)" : ""}</td></tr>}
            </tbody>
          </table></div>
          <div className="ma-card-b" style={{ paddingTop: 10 }}>
            <div className="ma-note" style={{ marginBottom: 0 }}>Three ways of saying one number: marketing says "get 12% back", finance books a 10.7% discount, the customer feels it as free units at the measured ₹/kWh. A slot with no ceiling closes the gap where a ₹40,600 top-up once earned nothing. Slots above 25% need a super-admin.</div>
          </div>
        </div>
      )}

      {/* ---- 3 limits */}
      {form.kind === "offer" && (
        <div className="ma-card" style={{ marginBottom: 14 }}>
          <div className="ma-card-h"><h3>3 · Limits</h3><span className="ma-hint">start from a shape, then adjust</span></div>
          <div className="ma-card-b">
            {editable && (
              <div className="ma-presets" style={{ marginBottom: 14, flexWrap: "wrap" }}>
                {PRESETS.map((p) => (
                  <button key={p.key} className={`ma-preset ${preset === p.key ? "on" : ""}`} onClick={() => { setPreset(p.key); setForm(p.apply(form)); setForecast(null); }} title={p.sub}>
                    {p.name}
                  </button>
                ))}
              </div>
            )}

            <div style={{ border: "1px solid #e6e8ee", borderLeft: "3px solid #1565c0", borderRadius: "0 10px 10px 0", padding: "12px 14px", background: "#f5f6fa" }}>
              <div className="ma-hint" style={{ textTransform: "uppercase", letterSpacing: ".4px", fontWeight: 600 }}>What will stop this campaign</div>
              {verdict ? (<>
                <div style={{ fontSize: 16, marginTop: 3 }}>{verdict.main}</div>
                <div className="ma-hint" style={{ marginTop: 3 }}>{verdict.sub}</div>
                {forecast && (
                  <div className="ma-bars" style={{ marginTop: 10 }}>
                    {forecast.window.max_awards != null && (
                      <div className="ma-bar"><span className="nm">Rewards {num(forecast.window.max_awards)}</span>
                        <span className="track"><span className="fill" style={{ width: `${Math.min(100, (forecast.window.topups / forecast.window.max_awards) * 100)}%`, background: binds?.limit === "max_awards" ? "#e65100" : "#1565c0" }} /></span>
                        <span className="val">{num(forecast.window.topups)} qualify</span></div>
                    )}
                    {forecast.window.budget_cap != null && (
                      <div className="ma-bar"><span className="nm">Budget {money(forecast.window.budget_cap)}</span>
                        <span className="track"><span className="fill" style={{ width: `${Math.min(100, forecast.window.budget_share_used || 0)}%`, background: binds?.limit === "budget_cap" ? "#e65100" : "#1565c0" }} /></span>
                        <span className="val">{money(forecast.window.payout)}</span></div>
                    )}
                    {(form.per_user_award_limit || form.per_user_cap) && (
                      <div className="ma-bar"><span className="nm">Per person</span>
                        <span className="track"><span className="fill" style={{ width: `${forecast.history.qualifying_users ? Math.min(100, (forecast.history.users_hit_allowance / Math.max(1, forecast.history.qualifying_users + forecast.history.users_hit_allowance)) * 100) : 0}%` }} /></span>
                        <span className="val">{num(forecast.history.users_hit_allowance)} would hit it</span></div>
                    )}
                  </div>
                )}
              </>) : (
                <div style={{ fontSize: 14, marginTop: 3, color: "#8a92a1" }}>Run the forecast to find out which limit bites first.</div>
              )}
            </div>

            <div style={{ border: "1px solid #e6e8ee", borderLeft: "3px solid #2e7d32", borderRadius: "0 10px 10px 0", padding: "10px 14px", background: "#f5f6fa", marginTop: 12 }}>
              <div className="ma-hint" style={{ textTransform: "uppercase", letterSpacing: ".4px", fontWeight: 600 }}>Reads to a customer as</div>
              <div style={{ fontSize: 14, marginTop: 2 }}>“{readsAs({
                max_awards: numOrNull(form.max_awards), budget_cap: numOrNull(form.budget_cap), per_user_award_limit: numOrNull(form.per_user_award_limit),
                per_user_cap: numOrNull(form.per_user_cap), per_user_reset: form.per_user_reset, max_cashback_cap: numOrNull(form.max_cashback_cap),
              })}”</div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 12, marginTop: 14 }}>
              {field("Total budget (₹)", inp("budget_cap", "number", "no limit"), "while funds last")}
              {field("Max rewards", inp("max_awards", "number", "no limit"), "first N recharges")}
              {field("Per person, times", sel("per_user_award_limit", [["", "No limit"], ["1", "1"], ["2", "2"], ["3", "3"], ["5", "5"], ["10", "10"]]))}
              {field("Per person, money (₹)", inp("per_user_cap", "number", "no limit"))}
              {field("Resets", sel("per_user_reset", [["campaign", "Never — over the whole campaign"], ["day", "Every day"], ["week", "Every week (Monday)"], ["month", "Every month"]]))}
              {field("Cap per reward (₹)", inp("max_cashback_cap", "number", "no cap"), "or: on the first ₹X of the recharge")}
            </div>

            <div style={{ marginTop: 12, borderTop: "1px solid #e6e8ee", paddingTop: 10 }}>
              <button className="ma-btn" onClick={() => setShowAdvanced((s) => !s)}>{showAdvanced ? "▾" : "▸"} Advanced — what happens at the edges</button>
              {showAdvanced && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12, marginTop: 12 }}>
                  {field("When a limit is reached", sel("on_budget_exhausted", [["fall_back", "Fall back to the standing offer"], ["stop_hard", "Stop paying entirely"], ["continue_and_alert", "Keep paying, alert me (no ceiling!)"]]))}
                  {field("Priority when campaigns overlap", inp("priority", "number", "auto — best rate for the customer"), "lower number wins; blank = customer gets the better offer")}
                  <div className="ma-note" style={{ gridColumn: "1 / -1", marginBottom: 0 }}>
                    <b>Fall back is the default.</b> Someone who tops up right after the budget runs out, or who has used their weekly rewards, drops to the standing card rather than zero. <b>Keep paying, alert me</b> is the only setting with no ceiling.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <CampaignPreview
        data={preview}
        tiers={tiers}
        kind={form.kind}
        loading={previewBusy}
        name={form.name}
        headline={form.headline}
        status={campaign?.status ?? null}
        theme={boost.theme || null}
        otherImages={otherImages}
      />

      {/* ---- the message: its own card, both kinds. It used to sit inside the
           Limits card's collapsed "Advanced" block, and that card renders only
           for offers -- so an announcement, which IS a message, had no way to
           carry a picture at all. The picture now has its own card below. */}
      <div className="ma-card" style={{ marginBottom: 14 }}>
        <div className="ma-card-h"><h3>{form.kind === "offer" ? "4" : "2"} · The announcement</h3><span className="ma-hint">when it goes out, and on which channels</span></div>
        <div className="ma-card-b">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12 }}>
                {!isAnnouncement && field("Teaser at (IST)", inp("teaser_at", "datetime-local"), "\u201cstarts tomorrow 8 pm\u201d while it is still scheduled. Blank + no announce time = sent when you schedule.")}
                {field("Announce at (IST, blank = at start)", inp("announce_at", "datetime-local"), isAnnouncement ? "when the message goes out" : "the headline, when the campaign opens")}
                {!isAnnouncement && field("Reminder at (IST, blank = none)", inp("reminder_at", "datetime-local"), "\u201cends tomorrow 11 pm\u201d before it closes")}
                {isAnnouncement && (
                  <div className="ma-note" style={{ gridColumn: "1 / -1", marginBottom: 0 }}>
                    <b>An announcement only sends once, when it goes live.</b> There is no opening to trail and no closing to warn about, so it has no teaser and no reminder.
                  </div>
                )}
                <label className="ma-select">Channels
                  <span style={{ display: "flex", gap: 14, fontSize: 13, color: "#1f2430", marginTop: 4 }}>
                    <label><input type="checkbox" checked={form.announce_push} disabled={!editable} onChange={(e) => patch({ announce_push: e.target.checked })} /> Push</label>
                    <label><input type="checkbox" checked={form.announce_inapp} disabled={!editable} onChange={(e) => patch({ announce_inapp: e.target.checked })} /> In-app bell</label>
                  </span>
                </label>
                {/* the stored start: the server judges a teaser against it, not the form's */}
                {!isAnnouncement && (
                  <CampaignTeasers
                    campaignId={id}
                    startsAt={campaign?.starts_at ?? null}
                    editable={teasersEditable}
                    reloadKey={campaign?.updated_at ?? null}
                    setErr={setErr}
                    setMsg={setMsg}
                  />
                )}
          </div>
        </div>
      </div>

      {/* ---- the artwork: the main image is the default, and the extra images
              take over by date, audience and A/B arm. Editable while live. */}
      <CampaignArtwork
        step={form.kind === "offer" ? "5" : "3"}
        campaignId={id}
        status={campaign?.status ?? null}
        dates={campaign && campaign.status !== "draft"
          ? { starts_at: campaign.starts_at, ends_at: campaign.ends_at }
          : { starts_at: istInputToUtcIso(form.starts_at), ends_at: istInputToUtcIso(form.ends_at) }}
        editable={artworkEditable}
        lateEdit={artworkLateEdit}
        main={mainImage}
        mainUrl={form.image_url.trim() || null}
        reloadKey={campaign?.updated_at ?? null}
        ensureCampaign={async () => id ?? (await saveDraft())}
        setErr={setErr}
        setMsg={setMsg}
        onCount={setOtherImages}
      />

      {/* ---- who can get it */}
      <div className="ma-card" style={{ marginBottom: 14 }}>
        <div className="ma-card-h"><h3>{form.kind === "offer" ? "6" : "4"} · Who can get it</h3><span className="ma-hint">visibility and the allowlist</span></div>
        <div className="ma-card-b">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 9 }}>
            {AUD.map((a) => (
              <label key={a.key} style={{ border: `1px solid ${form.audience === a.key ? "#5554d3" : "#e6e8ee"}`, borderRadius: 9, padding: "10px 12px", display: "flex", gap: 9, cursor: editable ? "pointer" : "default", background: "#fff" }}>
                <input type="radio" name="aud" checked={form.audience === a.key} disabled={!editable}
                  onChange={() => patch({ audience: a.key, visibility: a.key === "all" ? "public" : "private" })} style={{ marginTop: 3 }} />
                <span><b style={{ fontSize: 13 }}>{a.name}</b><div className="ma-hint">{a.sub}{audienceCounts?.[a.key] ? ` · ${num(audienceCounts[a.key].users)} people, ${num(audienceCounts[a.key].reachable)} reachable` : ""}</div></span>
              </label>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12, marginTop: 12 }}>
            {field("Visibility", sel("visibility", [["public", "Public — on the app's offer feed"], ["private", "Private — signed-in members only"]]), form.audience !== "all" ? "forced private for anything narrower than everyone" : undefined)}
            {field("Restrict to org ids (comma-separated, blank = all)", inp("org_ids", "text", "1, 12"))}
          </div>

          {(form.audience === "list" || memberCount > 0) && (
            <div style={{ marginTop: 14, border: "1px solid #e6e8ee", borderRadius: 9, overflow: "hidden" }}>
              <div style={{ background: "#fafbfc", padding: "8px 12px", fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 10 }}>
                Allowlist · {num(memberCount)} on the list
                <span className="ma-spacer" />
              </div>
              {editable && (
                <div className="ma-card-b">
                  <textarea value={phones} onChange={(e) => setPhones(e.target.value)} placeholder="Paste phone numbers — one per line or comma-separated"
                    style={{ width: "100%", minHeight: 70, border: "1px solid #e6e8ee", borderRadius: 7, padding: 8, fontSize: 13, fontFamily: "inherit" }} />
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <button className="ma-btn" disabled={busy === "resolve"} onClick={resolvePhones}>Check numbers</button>
                    {resolved && <button className="ma-btn primary" disabled={busy === "members" || !resolved.matched} onClick={addMembers}>Add {resolved.matched} matched</button>}
                  </div>
                  {resolved && (<>
                    <div style={{ display: "flex", gap: 8, margin: "10px 0" }}>
                      <span className="ma-pill ok">{resolved.matched} matched</span>
                      <span className="ma-pill warn">{resolved.unmatched} no account</span>
                      {resolved.overlapping > 0 && <span className="ma-pill warn">{resolved.overlapping} also in another private offer</span>}
                    </div>
                    <div className="ma-tablewrap"><table className="ma-table">
                      <thead><tr><th>Number</th><th>Name</th><th>Status</th><th>Note</th></tr></thead>
                      <tbody>{resolved.rows.map((r) => (
                        <tr key={r.phone}><td>{r.phone}</td><td>{r.user_name || "—"}</td>
                          <td>{r.matched ? <span className="ma-pill ok">✓ matched</span> : <span className="ma-pill warn">no account</span>}</td>
                          <td className="sub">{r.also_in.length ? `also in ${r.also_in.map((x) => x.name).join(", ")} — gets the better of the two` : r.organization || ""}</td></tr>
                      ))}</tbody>
                    </table></div>
                    <p className="ma-hint">Resolved on upload, not at run time: a number that matches nobody is visible while a person is still looking.</p>
                  </>)}
                </div>
              )}
            </div>
          )}
          <div className="ma-note" style={{ marginTop: 12, marginBottom: 0 }}>A private campaign never appears in the public feed. Members see it through the signed-in endpoint; everyone else keeps the standing offer and never learns it exists.</div>
        </div>
      </div>

      {/* ---- how often it shows. Editable while the campaign is live, which is
              the whole point of the separate endpoint. */}
      <div className="ma-card" style={{ marginBottom: 14 }}>
        <div className="ma-card-h">
          <h3>{form.kind === "offer" ? "7" : "5"} · How often it shows</h3>
          <span className="ma-hint">
            {!boostEditable ? "frozen" : boostLateEdit ? `${campaign?.status} \u2014 these caps stay editable` : "stays editable once live"}
          </span>
          {boostEditable && (
            <button className="ma-btn" disabled={!!busy || !boostDirty} onClick={saveBoost}>
              <Save size={14} /> {boostDirty ? "Save promotion" : "Saved"}
            </button>
          )}
        </div>
        <div className="ma-card-b">
          <label style={{ display: "flex", gap: 9, alignItems: "flex-start" }}>
            <input type="checkbox" checked={boost.enabled} disabled={!boostEditable}
              onChange={(e) => setBoost((b) => ({ ...b, enabled: e.target.checked }))} style={{ marginTop: 3 }} />
            <span><b style={{ fontSize: 13 }}>Promote this campaign</b>
              <div className="ma-hint">Kill switch. Off stops every surface at once; the offer still pays.</div></span>
          </label>

          <div className="ma-hint" style={{ margin: "14px 0 6px" }}>Surfaces it may use</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 9 }}>
            {SURFACES.map(([k, name, sub]) => (
              <label key={k} style={{ border: `1px solid ${boost.surfaces.includes(k) ? "#5554d3" : "#e6e8ee"}`, borderRadius: 9, padding: "10px 12px", display: "flex", gap: 9, cursor: boostEditable ? "pointer" : "default", background: "#fff" }}>
                <input type="checkbox" checked={boost.surfaces.includes(k)} disabled={!boostEditable}
                  onChange={() => toggleSurface(k)} style={{ marginTop: 3 }} />
                <span><b style={{ fontSize: 13 }}>{name}</b><div className="ma-hint">{sub}</div></span>
              </label>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12, marginTop: 14 }}>
            {field("Impressions per 7 days", bnum("per_7d"), boundHint("per_7d"))}
            {field("Hours between impressions", bnum("cooldown_hours"), `${boundHint("cooldown_hours")} (2\u201314 days)`)}
            {field("Lifetime per user", bnum("lifetime"), `${boundHint("lifetime")}, ever`)}
            {field("Offer priority", bnum("priority"), `${boundHint("priority")}. Which campaign is offered when several are eligible \u2014 not which one pays.`)}
            <label className="ma-select">Social proof
              <span style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, color: "#1f2430", marginTop: 4 }}>
                <input type="checkbox" checked={boost.social_proof} disabled={!boostEditable}
                  onChange={(e) => setBoost((b) => ({ ...b, social_proof: e.target.checked }))} />
                Show a claimed count
              </span>
              <span className="ma-hint">Stays hidden until the count reaches the minimum.</span>
            </label>
            {field("Social proof minimum", bnum("social_proof_min"), boundHint("social_proof_min"))}
            {field("Theme", (
              <select value={boost.theme} disabled={!boostEditable}
                onChange={(e) => setBoost((b) => ({ ...b, theme: e.target.value as BoostForm["theme"] }))}>
                <option value="">None</option>
                <option value="diwali">Diwali</option>
              </select>
            ))}
          </div>

          <div className="ma-note" style={{ marginTop: 14, marginBottom: 0 }}>
            <b>Fixed, not editable here:</b> 4 interruptions per user per rolling 7 days across all campaigns ·
            1 per session · 2 dismissals mute this campaign for 30 days, 3 for good · the Settings opt-out
            overrides everything · social proof never shows below 50.
          </div>
        </div>
      </div>

      {/* ---- 5 forecast + schedule */}
      {form.kind === "offer" && (
        <div className="ma-card" style={{ marginBottom: 14 }}>
          <div className="ma-card-h"><h3>8 · Forecast</h3><span className="ma-hint">last 90 days of real top-ups, replayed through this card</span>
            {canManage && <button className="ma-btn" disabled={!!busy} onClick={runForecast}><RefreshCw size={14} /> {forecast ? "Re-run" : "Run forecast"}</button>}</div>
          <div className="ma-card-b">
            {!forecast && <p className="ma-hint">Saves the draft, then replays the history. No dice: random bands pay their midpoint. No lift assumed.</p>}
            {forecast && (<>
              <div className="ma-kpis">
                <div className="ma-kpi"><div className="ma-kpi-label">Would pay out</div><div className="ma-kpi-value">{money(forecast.window.payout)}</div><div className="ma-kpi-sub">{forecast.history.share_of_gross}% of qualifying gross · {Math.round(windowDays)} days</div></div>
                <div className="ma-kpi"><div className="ma-kpi-label">Top-ups caught</div><div className="ma-kpi-value">{num(forecast.window.topups)}</div><div className="ma-kpi-sub">{forecast.vs_standing ? `vs ${num(forecast.vs_standing.standing_qualifying * (windowDays / forecast.history_days))} on today's card` : ""}</div></div>
                <div className="ma-kpi"><div className="ma-kpi-label">Users reached</div><div className="ma-kpi-value">{num(forecast.history.qualifying_users)}</div><div className="ma-kpi-sub">of {num(forecast.history.users_total)} who topped up</div></div>
                <div className="ma-kpi"><div className="ma-kpi-label">vs standing card</div><div className="ma-kpi-value" style={{ color: (forecast.vs_standing?.delta_window || 0) > 0 ? "#c62828" : "#2e7d32" }}>{forecast.vs_standing ? `${forecast.vs_standing.delta_window >= 0 ? "+" : ""}${money(forecast.vs_standing.delta_window)}` : "—"}</div><div className="ma-kpi-sub">what this change costs</div></div>
              </div>
              <div className="ma-tablewrap"><table className="ma-table">
                <thead><tr><th>Slot</th><th className="num">Qualifying top-ups</th><th className="num">Users</th><th className="num">Gross</th><th className="num">Forecast payout</th><th className="num">Share</th></tr></thead>
                <tbody>{forecast.per_slot.map((s) => (
                  <tr key={s.slot}><td>{s.slot}</td><td className="num">{num(s.topups)}</td><td className="num">{num(s.users)}</td><td className="num">{money(s.gross)}</td><td className="num">{money(s.payout)}</td><td className="num">{s.share_of_gross}%</td></tr>
                ))}</tbody>
              </table></div>
              <p className="ma-hint">Per {forecast.history_days} days of history, scaled to the {Math.round(windowDays)}-day window. Nothing before the start time will be paid at these rates.</p>
            </>)}

            {collisions.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Overlap <span className="ma-pill warn">{collisions.length} collision{collisions.length > 1 ? "s" : ""}</span></div>
                {collisions.map((c) => <CollisionCard key={c.campaign_id} c={c} />)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---- schedule conflicts */}
      {conflict?.detail === "lead_time_short" && (
        <div className="ma-card" style={{ marginBottom: 14, borderColor: "#e65100" }}>
          <div className="ma-card-h"><h3>⚠ {(conflict as LeadTimeShort).shortfall_hours} hours short</h3></div>
          <div className="ma-card-b">
            <p style={{ margin: "0 0 8px", fontSize: 13 }}>This start gives <b>{(conflict as LeadTimeShort).lead_hours_actual}h</b> of notice against a <b>{(conflict as LeadTimeShort).lead_hours_required}h</b> minimum. Either move the start, or ask a super-admin for an override with the reason on the record.</p>
            <textarea value={waiverReason} onChange={(e) => setWaiverReason(e.target.value)} placeholder="Why the rush? (required)" style={{ width: "100%", minHeight: 60, border: "1px solid #e6e8ee", borderRadius: 7, padding: 8, fontSize: 13, fontFamily: "inherit" }} />
            <span className="ma-hint" style={{ display: "block", marginTop: 4 }}>
              {waiverReason.trim().length < 10
                ? `At least 10 characters — ${waiverReason.trim().length} so far. It goes on the record with your name.`
                : "This goes on the record with your name."}
            </span>
            {waiverErr && (
              <div className="ma-error" style={{ marginTop: 8, marginBottom: 0 }}>{waiverErr}</div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button className="ma-btn primary" disabled={busy === "waiver"} onClick={raiseWaiver}>
                {busy === "waiver" ? "Requesting…" : "Request an override →"}
              </button>
              <button className="ma-btn" onClick={() => setConflict(null)}>Change the dates instead</button>
            </div>
          </div>
        </div>
      )}
      {conflict?.detail === "already_started" && (
        <div className="ma-card" style={{ marginBottom: 14, borderColor: "#d03b3b" }}>
          <div className="ma-card-h"><h3>⚠ The start time has already passed</h3></div>
          <div className="ma-card-b">
            <p style={{ margin: "0 0 8px", fontSize: 13 }}>
              Move the start into the future and schedule again. <b>An override cannot help here</b> — a
              waiver shortens the notice period, it does not let a campaign begin in the past, so scheduling
              refuses this before it ever looks for one.
            </p>
            <p className="ma-hint" style={{ margin: 0 }}>
              This is easy to hit when the start is only minutes away: the time drifts past while you are
              still filling the form in.
            </p>
          </div>
        </div>
      )}
      {conflict?.detail === "overlap" && (
        <div className="ma-card" style={{ marginBottom: 14, borderColor: "#e65100" }}>
          <div className="ma-card-h"><h3>This campaign overlaps another</h3></div>
          <div className="ma-card-b">
            {(conflict as OverlapConflict).collisions.map((c) => <CollisionCard key={c.campaign_id} c={c} />)}
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button className="ma-btn primary" disabled={!!busy} onClick={() => schedule(true)}>Keep this and schedule</button>
              <button className="ma-btn" onClick={() => setConflict(null)}>Change the dates</button>
            </div>
            <p className="ma-hint">By default the customer gets the best offer they qualified for. To make this one win regardless, set a priority under Advanced (lower number wins).</p>
          </div>
        </div>
      )}

      {editable && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 20 }}>
          <button className="ma-btn primary" disabled={!!busy} onClick={() => schedule(false)}><Play size={14} /> Schedule</button>
          <span className="ma-hint">Freezes the rate card, resolves the audience, stores the forecast. After this the campaign cannot be edited.</span>
        </div>
      )}

      {/* ---- trail */}
      {detail && detail.audit.length > 0 && (
        <div className="ma-card" style={{ marginBottom: 14 }}>
          <div className="ma-card-h"><h3>The trail</h3><span className="ma-hint">append-only</span></div>
          <div className="ma-tablewrap"><table className="ma-table">
            <thead><tr><th>When</th><th>What</th><th>Who</th><th>Detail</th></tr></thead>
            <tbody>{detail.audit.slice(0, 40).map((a) => (
              <tr key={a.id}><td className="sub">{ts(a.at)}</td><td><b>{a.action}</b>{a.to_status ? <span className="sub"> → {a.to_status}</span> : null}</td><td className="sub">{a.actor}</td>
                <td className="sub" style={{ whiteSpace: "normal", maxWidth: 420 }}>{a.detail ? JSON.stringify(a.detail).slice(0, 160) : ""}</td></tr>
            ))}</tbody>
          </table></div>
        </div>
      )}
    </div>
  );
};

const CollisionCard: React.FC<{ c: Collision }> = ({ c }) => (
  <div style={{ border: "1px solid #e6e8ee", borderLeft: "3px solid #1565c0", borderRadius: "0 9px 9px 0", padding: "10px 14px", background: "#f5f6fa", marginBottom: 8 }}>
    <div className="ma-hint" style={{ textTransform: "uppercase", letterSpacing: ".4px", fontWeight: 600 }}>{ts(c.overlap_start)} → {ts(c.overlap_end)} · overlaps <b>{c.name}</b> ({c.status})</div>
    <div style={{ fontSize: 15, marginTop: 3 }}><b>{c.winner} wins</b> <span className="ma-hint">— {c.decided_by === "priority" ? "by explicit priority" : "better rate for the customer"}</span></div>
    <div className="ma-hint" style={{ marginTop: 3 }}>About <b>{num(c.topups_est)} top-ups</b> fall in that window. They are paid <b>{money(Math.abs(c.delta_rupees))} {c.delta_rupees >= 0 ? "more" : "less"}</b> than under {c.name} alone. Nobody is paid less than they would have been.</div>
  </div>
);

export default CampaignBuilder;
